import {
  Box,
  Button,
  Icon,
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Typography,
} from '@veraclins-dev/ui';
import { cn } from '@veraclins-dev/utils';

import { ThemeToggle } from '@veraclins/ui';

import { AppLink } from '../link';
import { AppNavLink } from '../nav-link';

interface HeaderProps {
  className?: string;
}

const navItems = [
  { to: '/', label: 'Home' },
  { to: '/work', label: 'Work' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
] as const;

const navClassName =
  "relative py-0.5 text-sm/normal font-medium text-foreground/70 transition-colors hover:text-foreground aria-[current=page]:text-foreground aria-[current=page]:after:absolute aria-[current=page]:after:inset-x-0 aria-[current=page]:after:-bottom-0.5 aria-[current=page]:after:h-0.5 aria-[current=page]:after:rounded-sm aria-[current=page]:after:bg-primary aria-[current=page]:after:content-['']";

const mobileNavClassName =
  // oxlint-disable-next-line veraclins-ui/no-arbitrary-style -- 21px display label, 13px/4px padding and primary-tinted divider match the design handoff and have no scale-token/prop equivalent
  'w-full justify-start border-b border-primary/10 px-1 py-[13px] text-left font-sans text-xl/normal font-bold tracking-normal text-foreground transition-colors last:border-b-0 hover:text-primary aria-[current=page]:text-primary';

export function Header({ className }: HeaderProps) {
  return (
    <Box
      component="header"
      display="flex"
      flexWrap="wrap"
      items="center"
      justify="between"
      gapX={4}
      gapY={3}
      borderBottom={1}
      // oxlint-disable-next-line veraclins-ui/prefer-style-props -- primary-tinted border color and 14px vertical padding have no scale-token/prop equivalent
      className={cn('border-primary/14 py-3.5', className)}
      data-testid="header"
    >
      <AppLink
        to="/"
        underline="none"
        tone="neutral"
        className="flex items-center gap-2.5 text-foreground max-lg:mr-auto"
      >
        <Box
          component="span"
          aria-hidden="true"
          display="flex"
          items="center"
          justify="center"
          rounded="full"
          size={10}
          // oxlint-disable-next-line veraclins-ui/no-arbitrary-style -- the CA badge gradient and 15px glyph size are brand-specific and have no theme token
          className="bg-[linear-gradient(135deg,var(--primary),color-mix(in_oklch,var(--primary)_60%,#b33))] text-base font-extrabold text-primary-foreground"
        >
          CA
        </Box>
        <Typography
          component="span"
          variant="inherit"
          className="text-base font-bold tracking-normal"
        >
          Clinton Agada
        </Typography>
      </AppLink>
      <Box
        component="nav"
        items="center"
        gap={7}
        // oxlint-disable-next-line veraclins-ui/prefer-style-props -- responsive hidden→flex display toggle has no single style-prop equivalent
        className="hidden lg:flex"
        aria-label="Primary navigation"
      >
        {navItems.map((item) => (
          <AppNavLink
            key={item.to}
            to={item.to}
            className={navClassName}
            end={item.to === '/'}
          >
            {item.label}
          </AppNavLink>
        ))}
      </Box>
      <Box
        display="flex"
        items="center"
        gap={4}
        order={3}
        // oxlint-disable-next-line veraclins-ui/prefer-style-props -- responsive lg:order-none reset has no style-prop equivalent
        className="lg:order-none"
      >
        <ThemeToggle className="size-9 rounded-full border border-primary/18 text-foreground transition hover:border-foreground" />
        <AppLink
          to="/contact"
          underline="none"
          tone="neutral"
          className="hidden items-center justify-center rounded-lg border border-primary bg-primary px-3.5 py-2 text-sm/normal font-semibold text-primary-foreground transition hover:-translate-y-px sm:inline-flex"
        >
          Say hello
        </AppLink>
      </Box>
      <Sheet>
        <SheetTrigger asChild>
          <Button
            type="button"
            emphasis="outline"
            aria-label="Open menu"
            buttonSize="icon"
            order={2}
            p={0}
            // oxlint-disable-next-line veraclins-ui/prefer-style-props -- arbitrary h/w sizes and primary-tinted border have no scale-token/prop equivalent
            className="h-9.5 w-10.5 rounded-lg border-primary/18 bg-transparent lg:hidden"
            data-testid="mobile-menu-trigger"
          >
            <Icon name="bars-3" size="sm" />
          </Button>
        </SheetTrigger>
        <SheetContent
          side="right"
          // oxlint-disable-next-line veraclins-ui/prefer-style-props -- SheetContent (Radix primitive) has no style props
          className="border-primary/16 bg-background px-5.5 pt-5 pb-7"
        >
          <SheetHeader className="mb-6 text-left">
            <SheetTitle className="font-sans text-base font-bold tracking-normal">
              Clinton Agada
            </SheetTitle>
          </SheetHeader>
          <Box
            component="nav"
            display="flex"
            flexDirection="column"
            aria-label="Mobile navigation"
          >
            {navItems.map((item) => (
              <SheetClose key={item.to} asChild>
                <AppNavLink
                  to={item.to}
                  type="link"
                  className={mobileNavClassName}
                  end={item.to === '/'}
                >
                  {item.label}
                </AppNavLink>
              </SheetClose>
            ))}
          </Box>
          <SheetFooter className="mt-auto gap-4">
            <ThemeToggle className="flex w-full items-center justify-start gap-2.5 rounded-none border-0 px-0 font-sans text-sm font-semibold text-foreground/80 [&_svg]:size-4" />
            <SheetClose asChild>
              <AppLink
                to="/contact"
                underline="none"
                tone="neutral"
                className="inline-flex items-center justify-center rounded-lg bg-primary px-5.5 py-3 font-sans text-base font-semibold text-primary-foreground transition hover:-translate-y-px"
              >
                Say hello
              </AppLink>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </Box>
  );
}
